// Color helpers and harmony generators for the palette panel.
// All RGB values are in 0..1 ranges. All HSL values are in 0..1 ranges.

export function hexToRgb01(hex) {
  let h = String(hex || '').trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return [1, 1, 1];
  const n = parseInt(h, 16);
  return [
    ((n >> 16) & 0xff) / 255,
    ((n >> 8) & 0xff) / 255,
    (n & 0xff) / 255,
  ];
}

export function rgb01ToHex([r, g, b]) {
  const c = (v) => Math.round(Math.max(0, Math.min(1, v)) * 255)
    .toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}

export function rgbToHsl(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hue2rgb(p, q, t) {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
}

export function hslToRgb(h, s, l) {
  let r;
  let g;
  let b;
  if (s === 0) {
    r = l; g = l; b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hh = ((h % 1) + 1) % 1;
    r = hue2rgb(p, q, hh + 1 / 3);
    g = hue2rgb(p, q, hh);
    b = hue2rgb(p, q, hh - 1 / 3);
  }
  return [r, g, b];
}

function stopFromHsl(h, s, l) {
  return hslToRgb(((h % 1) + 1) % 1, Math.max(0, Math.min(1, s)), Math.max(0, Math.min(1, l)));
}

export function harmonyAnalogous(hex) {
  const [h, s, l] = rgbToHsl(...hexToRgb01(hex));
  return [
    stopFromHsl(h - 0.08, s, l),
    stopFromHsl(h, s, l),
    stopFromHsl(h + 0.08, s, l),
  ];
}

export function harmonyComplementary(hex) {
  const [h, s, l] = rgbToHsl(...hexToRgb01(hex));
  return [
    stopFromHsl(h, s, l),
    stopFromHsl(h + 0.5, s, l),
    stopFromHsl(h - 0.08, s, Math.max(0, Math.min(1, l * 0.9))),
  ];
}

export function harmonyTriadic(hex) {
  const [h, s, l] = rgbToHsl(...hexToRgb01(hex));
  return [
    stopFromHsl(h, s, l),
    stopFromHsl(h + 1 / 3, s, l),
    stopFromHsl(h + 2 / 3, s, l),
  ];
}

export function harmonySplitComp(hex) {
  const [h, s, l] = rgbToHsl(...hexToRgb01(hex));
  return [
    stopFromHsl(h, s, l),
    stopFromHsl(h + 0.5 - 0.08, s, l),
    stopFromHsl(h + 0.5 + 0.08, s, l),
  ];
}

export function harmonyMonochromatic(hex) {
  const [h, s] = rgbToHsl(...hexToRgb01(hex));
  return [
    stopFromHsl(h, s, 0.3),
    stopFromHsl(h, s, 0.55),
    stopFromHsl(h, s, 0.8),
  ];
}

export function harmonyChaos(hex) {
  const [h, s, l] = rgbToHsl(...hexToRgb01(hex));
  const rnd = () => Math.random();
  return [
    stopFromHsl(h + (rnd() - 0.5) * 0.6, Math.max(0.3, s + (rnd() - 0.5) * 0.4),
      Math.max(0.15, Math.min(0.85, l + (rnd() - 0.5) * 0.4))),
    stopFromHsl(h + (rnd() - 0.5) * 0.6, Math.max(0.3, s + (rnd() - 0.5) * 0.4),
      Math.max(0.15, Math.min(0.85, l + (rnd() - 0.5) * 0.4))),
    stopFromHsl(h + (rnd() - 0.5) * 0.6, Math.max(0.3, s + (rnd() - 0.5) * 0.4),
      Math.max(0.15, Math.min(0.85, l + (rnd() - 0.5) * 0.4))),
  ];
}

export const HARMONIES = {
  analogous: harmonyAnalogous,
  complementary: harmonyComplementary,
  triadic: harmonyTriadic,
  splitComp: harmonySplitComp,
  monochromatic: harmonyMonochromatic,
  chaos: harmonyChaos,
};

// K-means in RGB space, k=3.
// pixels: flat Uint8ClampedArray (RGBA), or array of [r,g,b] in 0..1.
// Returns 3 centroids sorted by lightness, each [r,g,b] in 0..1.
export function kmeans3(pixels, opts = {}) {
  const iters = opts.iters ?? 30;
  // Normalize input into Array of [r,g,b] in 0..1.
  const pts = [];
  if (pixels && pixels.length && typeof pixels[0] === 'number') {
    for (let i = 0; i + 3 < pixels.length; i += 4) {
      pts.push([pixels[i] / 255, pixels[i + 1] / 255, pixels[i + 2] / 255]);
    }
  } else if (Array.isArray(pixels)) {
    for (const p of pixels) pts.push([p[0], p[1], p[2]]);
  }
  if (pts.length === 0) return [[0, 0, 0], [0.5, 0.5, 0.5], [1, 1, 1]];

  // init centroids by sampling 3 random pixels
  const k = 3;
  const centroids = [];
  for (let i = 0; i < k; i += 1) {
    centroids.push(pts[Math.floor(Math.random() * pts.length)].slice());
  }

  for (let it = 0; it < iters; it += 1) {
    const sums = [[0, 0, 0], [0, 0, 0], [0, 0, 0]];
    const counts = [0, 0, 0];
    for (const p of pts) {
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < k; c += 1) {
        const dr = p[0] - centroids[c][0];
        const dg = p[1] - centroids[c][1];
        const db = p[2] - centroids[c][2];
        const d = dr * dr + dg * dg + db * db;
        if (d < bestD) { bestD = d; best = c; }
      }
      sums[best][0] += p[0]; sums[best][1] += p[1]; sums[best][2] += p[2];
      counts[best] += 1;
    }
    for (let c = 0; c < k; c += 1) {
      if (counts[c] > 0) {
        centroids[c][0] = sums[c][0] / counts[c];
        centroids[c][1] = sums[c][1] / counts[c];
        centroids[c][2] = sums[c][2] / counts[c];
      }
    }
  }

  // sort by lightness (HSL l)
  centroids.sort((a, b) => {
    const la = rgbToHsl(a[0], a[1], a[2])[2];
    const lb = rgbToHsl(b[0], b[1], b[2])[2];
    return la - lb;
  });
  return centroids;
}
